import React from "react";

// Source
import * as HelperSrc from "../HelperSrc";
import StoreLocalization from "../store/Localization";
import StorePreference from "../store/Preference";
import StoreSetting from "../store/Setting";
import * as ModelLocalization from "../model/Localization";
import * as ModelPreference from "../model/Preference";
import * as ModelSetting from "../model/Setting";
import * as ModelDatepicker from "../model/Datepicker";

export default class ControllerDatepicker {
    // Variable
    private props: ModelDatepicker.Iprops;

    private storeLocalization: ModelLocalization.Istore<ModelLocalization.Idata>;
    private storageLocalizationData = React.useRef<ModelLocalization.Idata | undefined>(undefined);
    private storePreference: ModelPreference.Istore<ModelPreference.Idata>;
    private storagePreferenceData = React.useRef<ModelPreference.Idata | undefined>(undefined);
    private storeSetting: ModelSetting.Istore<ModelSetting.Idata>;
    private storageSettingData = React.useRef<ModelSetting.Idata | undefined>(undefined);

    private elementViewMain = React.useRef<HTMLElement | null>(null);
    private elementInput = React.useRef<HTMLInputElement | null>(null);
    private elementTooltip = React.useRef<HTMLElement | null>(null);
    private elementTooltipList = React.useRef<HTMLElement[] | null>(null);

    private isRendered = React.useRef<boolean>(false);
    private language = React.useRef<string>("");
    private limitYear = React.useRef<number>(50);
    private mode = React.useRef<string>("");
    private year = React.useRef<string>("");
    private month = React.useRef<string>("");
    private day = React.useRef<string>("");
    private inputValueInitial = React.useRef<string>("");
    private locale = React.useRef<string>("");

    getStorageLocalization: ModelLocalization.Idata | undefined;
    getStoragePreference: ModelPreference.Idata | undefined;

    getMode: string | undefined;
    setMode: React.Dispatch<React.SetStateAction<string | undefined>>;

    getYear: string | undefined;
    setYear: React.Dispatch<React.SetStateAction<string | undefined>>;

    getMonth: string | undefined;
    setMonth: React.Dispatch<React.SetStateAction<string | undefined>>;

    getDay: string | undefined;
    setDay: React.Dispatch<React.SetStateAction<string | undefined>>;

    getLanguage: string;
    setLanguage: React.Dispatch<React.SetStateAction<string>>;

    getLocale: string;
    setLocale: React.Dispatch<React.SetStateAction<string>>;

    // Method
    constructor(props: ModelDatepicker.Iprops, isRendered: boolean) {
        this.props = props;

        this.storeLocalization = new StoreLocalization();
        this.storageLocalizationData.current = this.storeLocalization.storageGet();
        this.storePreference = new StorePreference();
        this.storagePreferenceData.current = this.storePreference.storageGet();
        this.storeSetting = new StoreSetting();
        this.storageSettingData.current = this.storeSetting.storageGet();

        this.isRendered.current = isRendered;
        this.language.current = props.language || (this.storagePreferenceData.current ? this.storagePreferenceData.current.language : "");

        const [getStorageLocalization] = React.useState<ModelLocalization.Idata | undefined>(this.storageLocalizationData.current);
        this.getStorageLocalization = getStorageLocalization;
        const [getStoragePreference] = React.useState<ModelPreference.Idata | undefined>(this.storagePreferenceData.current);
        this.getStoragePreference = getStoragePreference;

        const [getMode, setMode] = React.useState<string | undefined>(undefined);
        this.getMode = getMode;
        this.setMode = setMode;

        const [getYear, setYear] = React.useState<string | undefined>(undefined);
        this.getYear = getYear;
        this.setYear = setYear;

        const [getMonth, setMonth] = React.useState<string | undefined>(undefined);
        this.getMonth = getMonth;
        this.setMonth = setMonth;

        const [getDay, setDay] = React.useState<string | undefined>(undefined);
        this.getDay = getDay;
        this.setDay = setDay;

        const [getLanguage, setLanguage] = React.useState<string>(this.language.current);
        this.getLanguage = getLanguage;
        this.setLanguage = setLanguage;

        const [getLocale, setLocale] = React.useState<string>(this.locale.current);
        this.getLocale = getLocale;
        this.setLocale = setLocale;
    }

    initializeHook = (): void => {
        React.useEffect(() => {
            HelperSrc.domObserver(`#view_datepicker_${this.props.id || ""}`, () => {
                this.initializeHtmlElement();

                document.onclick = (event) => {
                    const element = event.target as HTMLElement;

                    if (!element.classList.contains("MuiButtonBase-root")) {
                        const elementParent = HelperSrc.findElementParent(element, "view_datepicker");
                        const elementParentTooltip = elementParent ? elementParent.querySelector<HTMLElement>(".tooltip") : undefined;

                        if (this.elementTooltipList.current) {
                            for (const elementTooltip of this.elementTooltipList.current) {
                                if (elementParentTooltip !== elementTooltip) {
                                    elementTooltip.style.display = "none";
                                }
                            }
                        }
                    }
                };

                if (this.elementInput.current) {
                    this.elementInput.current.oninput = () => {
                        if (this.elementTooltip.current) {
                            this.elementTooltip.current.style.display = "none";
                        }
                    };
                }
            });
        }, []);

        React.useEffect(() => {
            this.storageLocalizationData.current = this.storeLocalization.storageGet();
            this.storagePreferenceData.current = this.storePreference.storageGet();
            this.storageSettingData.current = this.storeSetting.storageGet();

            if (this.storageLocalizationData.current) {
                this.language.current =
                    this.props.language || (this.storagePreferenceData.current ? this.storagePreferenceData.current.language : "");
                this.setLanguage(this.language.current);
            }

            if (this.storagePreferenceData.current) {
                const newLocale = `${this.storagePreferenceData.current.language}-${this.storagePreferenceData.current.country.toUpperCase()}`;

                if (this.locale.current !== newLocale) {
                    this.locale.current = newLocale;
                    this.setLocale(this.locale.current);
                }
            }
        }, [this.storageLocalizationData.current, this.storagePreferenceData.current, this.storageSettingData.current]);
    };

    clickOpen = () => {
        if (this.elementViewMain.current) {
            this.mode.current = this.props.fieldMode ? this.props.fieldMode.split("_")[1] : "";
            this.setMode(this.mode.current);

            if (this.elementInput.current) {
                this.inputValueInitial.current = this.elementInput.current.value;

                let inputValue = "";
                let dateCheck = 0;

                if (this.elementInput.current.value === "") {
                    const date = new Date();
                    inputValue = `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
                } else {
                    const date = this.elementInput.current.value;
                    inputValue = this.checkTextDate(date);
                }

                dateCheck = new Date(inputValue) as unknown as number;

                if (!isNaN(dateCheck)) {
                    const formatDateStandardSplit = inputValue.split("-");

                    this.year.current = formatDateStandardSplit[0].padStart(2, "0");
                    this.setYear(this.year.current);

                    if (this.mode.current === "yearMonth" || this.mode.current === "yearMonthDay") {
                        this.month.current = formatDateStandardSplit[1].padStart(2, "0");
                        this.setMonth(this.month.current);
                    }

                    if (this.mode.current === "yearMonthDay") {
                        this.day.current = formatDateStandardSplit[2].padStart(2, "0");
                        this.setDay(this.day.current);
                    }

                    if (this.elementTooltipList.current && this.elementTooltip.current) {
                        for (const elementTooltip of this.elementTooltipList.current) {
                            elementTooltip.style.display = "none";
                        }

                        this.elementTooltip.current.style.setProperty("display", "grid", "important");
                    }
                }
            }
        }
    };

    clickItem = (mode: string, action: string) => {
        if (mode === "year") {
            const currentYear = new Date().getFullYear();
            const year = action === "up" ? parseInt(this.year.current) + 1 : parseInt(this.year.current) - 1;

            if (year >= currentYear - this.limitYear.current && year <= currentYear + this.limitYear.current) {
                this.year.current = year.toString().trim();
                this.setYear(this.year.current);
            }
        } else if (mode === "month") {
            const month = action === "up" ? parseInt(this.month.current) + 1 : parseInt(this.month.current) - 1;

            if (month >= 1 && month <= 12) {
                this.month.current = month.toString().padStart(2, "0").trim();
                this.setMonth(this.month.current);
            }
        } else if (mode === "day") {
            const day = action === "up" ? parseInt(this.day.current) + 1 : parseInt(this.day.current) - 1;

            if (day >= 1 && day <= 31) {
                this.day.current = day.toString().padStart(2, "0").trim();
                this.setDay(this.day.current);
            }
        }
    };

    clickConfirm = () => {
        let result = "";

        if (this.storageSettingData.current) {
            this.language.current = this.language.current.split("_")[0];

            if (!/(?!\/)(?!-)[^\d\s]/.test(this.inputValueInitial.current)) {
                if (this.props.language) {
                    if (this.language.current === this.storageSettingData.current.language_ja) {
                        result += `${this.year.current}年`;

                        if (this.month.current !== "") {
                            result += `${this.month.current}月`;
                        }

                        if (this.day.current !== "") {
                            result += `${this.day.current}日`;
                        }
                    } else if (this.language.current === this.storageSettingData.current.language_en) {
                        if (this.month.current !== "") {
                            result += `${this.monthMapping(parseInt(this.month.current))},`;
                        }

                        if (this.day.current !== "") {
                            result += ` ${this.day.current}`;
                        }

                        result += ` ${this.year.current}`;
                    }
                } else {
                    const date = new Date(
                        `${this.year.current}/${this.month.current !== "" ? this.month.current : "1"}/${this.day.current !== "" ? this.day.current : "1"}`
                    );
                    const isMonth = this.month.current !== "" ? true : false;
                    const isDay = this.day.current !== "" ? true : false;
                    result = HelperSrc.localeFormat(date, isMonth, isDay, false) || "";
                }
            } else {
                if (this.language.current === this.storageSettingData.current.language_ja) {
                    result = this.inputValueInitial.current
                        .replace(/(\d{4})?年/, () => {
                            return `${this.year.current}年`;
                        })
                        .replace(/(\d{1,2})?月/, () => {
                            return `${parseInt(this.month.current)}月`;
                        })
                        .replace(/(\d{1,2})?日/, () => {
                            return `${parseInt(this.day.current)}日`;
                        });
                } else if (this.language.current === this.storageSettingData.current.language_en) {
                    result = this.inputValueInitial.current
                        .replace(/\b(?!FY)[a-zA-Z,)]+/, () => {
                            return this.month.current !== "" ? `${this.monthMapping(parseInt(this.month.current))},` : "";
                        })
                        .replace(/(?<= )[\d{2}]+(?= )/, () => {
                            return this.day.current !== "" ? `${this.day.current}` : "";
                        })
                        .replace(/(\d{4})/, () => {
                            return `${this.year.current}`;
                        });
                }
            }
        }

        if (this.elementInput.current) {
            this.nativeInputSetter(this.elementInput.current, result);

            if (this.elementTooltip.current) {
                this.elementTooltip.current.style.display = "none";
            }
        }
    };

    private nativeInputSetter = (input: HTMLInputElement, value: string) => {
        const prototype = Object.getPrototypeOf(input);
        const { set: prototypeValueSetter } = Object.getOwnPropertyDescriptor(prototype, "value") || {};
        const { set: valueSetter } = Object.getOwnPropertyDescriptor(input, "value") || {};

        if (prototypeValueSetter) {
            prototypeValueSetter.call(input, value);
        } else if (valueSetter) {
            valueSetter.call(input, value);
        } else {
            input.value = value;
        }

        input.dispatchEvent(new Event("input", { bubbles: true }));
    };

    private checkTextDate = (value: string) => {
        const regularExpression = new RegExp("[^\\d\\s\\-\\/]+");
        let date = "";

        if (regularExpression.test(value)) {
            const valueReplace = value
                .replace(/年|月|日/g, "/")
                .replace(/期|度|FY|版/g, "")
                .replace(/\/+$/, "");

            if (valueReplace === "") {
                date = new Date().toDateString();
            } else {
                date = new Date(valueReplace).toDateString();
            }
        } else {
            date = value;
        }

        const dateObject = new Date(date);

        const year = dateObject.getFullYear().toString();
        const month = (dateObject.getMonth() + 1).toString().padStart(2, "0");
        const day = dateObject.getDate().toString().padStart(2, "0");

        return `${year}-${month}-${day}`.trim();
    };

    private monthMapping = (value: number) => {
        if (this.storageLocalizationData.current) {
            switch (value) {
                case 1:
                    return this.storageLocalizationData.current.month_1;
                case 2:
                    return this.storageLocalizationData.current.month_2;
                case 3:
                    return this.storageLocalizationData.current.month_3;
                case 4:
                    return this.storageLocalizationData.current.month_4;
                case 5:
                    return this.storageLocalizationData.current.month_5;
                case 6:
                    return this.storageLocalizationData.current.month_6;
                case 7:
                    return this.storageLocalizationData.current.month_7;
                case 8:
                    return this.storageLocalizationData.current.month_8;
                case 9:
                    return this.storageLocalizationData.current.month_9;
                case 10:
                    return this.storageLocalizationData.current.month_10;
                case 11:
                    return this.storageLocalizationData.current.month_11;
                case 12:
                    return this.storageLocalizationData.current.month_12;
                default:
                    return "";
            }
        }
    };

    private initializeHtmlElement = (): void => {
        if (!this.elementViewMain.current) {
            this.elementInput.current = document.querySelector<HTMLInputElement>(`input[id='${this.props.id || ""}']`);
            this.elementTooltip.current = document.querySelector<HTMLElement>(`#view_datepicker_${this.props.id || ""} .tooltip`);
            this.elementTooltipList.current = Array.from(document.querySelectorAll<HTMLElement>(".view_datepicker .tooltip"));
            this.elementViewMain.current = document.querySelector<HTMLElement>(`#view_datepicker_${this.props.id || ""}`);
        }
    };
}
